import React, { useCallback, useEffect, useRef, useState } from "react";
import Autosuggest from "react-autosuggest";
import { Box } from "theme-ui";

import PropTypes from "prop-types";
import { TextField } from "office-ui-fabric-react";
import { debounce, get } from "lodash";

function shouldRenderSuggestions() {
  return true;
}

const renderInputComponent = ({
  showClose,
  clearIt,
  disabled,
  onChange,
  loading,
  ...inputProps
}) => (
  <TextField
    sx={{
      color: "text",
      ...(showClose && !disabled
        ? { ":disabled": { backgroundColor: "background" } }
        : {}),
    }}
    iconProps={
      loading
        ? {
            iconName: "Sync",
            style: { animation: "spin 2s linear infinite" },
          }
        : showClose &&
          !disabled && {
            iconName: "Cancel",
            style: { pointerEvents: "auto", cursor: "pointer" },
            onClick: clearIt,
          }
    }
    {...inputProps}
    {...{
      onChange: showClose ? clearIt : onChange,
    }}
    disabled={disabled}
  />
);

const useFetchSuggestions = (config, onChange, onBlur, value) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState(config?.dflt);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const abortControllerRef = useRef(null);
  const resetSelection = useCallback(() => {
    setSelected(null);
    setInputValue("");
    if (value) {
      onChange(undefined, null);
    }
  }, [value, onChange]);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchId = useCallback(
    (propsValue, propsValueDisplay) => {
      if (propsValue) {
        if (propsValueDisplay) {
          setInputValue(propsValueDisplay);
        }
        config?.fetch(propsValue, propsValueDisplay).then((result) => {
          if (isMountedRef.current) {
            setSelected(result);
            setInputValue(config?.getSuggestionValue(result));
          }
        });
      } else {
        resetSelection();
      }
    },
    [config, resetSelection]
  );

  const onChangeS = useCallback((event, { newValue }) => {
    const cleanedValue = newValue.replace(/^\s+/, "").replace(/\s+$/, " ");
    setInputValue(cleanedValue);
  }, []);

  const onBlurS = useCallback(() => {
    setInputValue(selected ? inputValue : "");
    if (onBlur && typeof onBlur === "function") {
      onBlur();
    }
  }, [selected, inputValue, onBlur]);

  const ongoingRequests = useRef(0);

  const getSuggestions = async (ivalue) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    ongoingRequests.current += 1;
    setLoading(true);

    try {
      const itrimValue = ivalue.trim().toLowerCase();
      if (!itrimValue) {
        ongoingRequests.current -= 1;
        if (ongoingRequests.current === 0) setLoading(false);
        return { count: 0, results: config?.dflt };
      }

      const response = await fetch(config.api(itrimValue), {
        ...get(config, "apiParams", () => ({}))(),
        signal,
      });
      const resData = await response.json();
      const data = Array.isArray(resData) ? { results: resData } : resData;

      if (data?.results) {
        let results = config?.dflt;
        if (config?.data2results) {
          results = config.data2results(
            data,
            itrimValue,
            config.getSuggestionValue,
            config.dflt
          );
        } else if (config?.data2resultsAsync) {
          results = await config.data2resultsAsync(
            data,
            itrimValue,
            config.getSuggestionValue,
            config.dflt
          );
        }

        ongoingRequests.current -= 1;
        if (ongoingRequests.current === 0) setLoading(false);
        return { count: results.length, results };
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        // console.error("Error fetching suggestions:", e.message);
      }
    }

    ongoingRequests.current -= 1;
    if (ongoingRequests.current === 0) setLoading(false);
    return { count: 0, results: config?.dflt };
  };

  const onSuggestionsClearRequested = useCallback(() => {
    setSuggestions(config?.dflt);
  }, [config]);

  const debouncedGetSuggestions = useCallback(
    debounce(async (ivalue) => {
      const itrimValue = ivalue.trim().toLowerCase();
      if (itrimValue.trim().length < 3) return;
      const data = await getSuggestions(itrimValue);
      if (!data?.results?.length) {
        onSuggestionsClearRequested();
      } else {
        setSuggestions(data.results);
      }
    }, 500),
    [getSuggestions, onSuggestionsClearRequested]
  );

  const onSuggestionsFetchRequested = useCallback(
    ({ value: itrimValue }) => {
      debouncedGetSuggestions(itrimValue);
    },
    [debouncedGetSuggestions]
  );

  const onSuggestionSelected = useCallback(
    (event, { suggestion }) => {
      if (
        onChange &&
        typeof onChange === "function" &&
        suggestion &&
        suggestion.id
      ) {
        onChange(suggestion.id, suggestion);
      }
      setSelected(suggestion);
    },
    [onChange]
  );

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    inputValue,
    selected,
    suggestions,
    loading,
    fetchId,
    onChangeS,
    onBlurS,
    getSuggestions,
    onSuggestionsFetchRequested,
    onSuggestionsClearRequested,
    onSuggestionSelected,
    resetSelection,
  };
};

function ServerAutoSuggest({
  config,
  value,
  onChange,
  onBlur,
  valueDisplay,
  renderPrint,
  ...restprops
}) {
  const {
    inputValue,
    suggestions,
    loading,
    fetchId,
    onChangeS,
    onBlurS,
    onSuggestionsFetchRequested,
    onSuggestionsClearRequested,
    onSuggestionSelected,
    resetSelection,
  } = useFetchSuggestions(config, onChange, onBlur, value);

  useEffect(() => {
    fetchId(value, valueDisplay);
  }, [value]);

  if (renderPrint) {
    return renderPrint(inputValue);
  }

  return (
    <Box variant="boxes.autosuggest">
      <style>
        {`
    .autosuggest-scrollbar {
      max-height: 170px;
      overflow-y: auto;
      background-color: #ffffff; /* White background */
    }

    .autosuggest-list-reset {
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .autosuggest-suggestion {
      padding: 8px 12px;
      cursor: pointer;
      background-color:rgb(247, 247, 247);
    }

    .autosuggest-suggestion-highlighted {
      background-color:rgb(255, 255, 255);
    }
    .ms-Panel-main{
    width:90%}
  `}
      </style>

      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={config?.getSuggestionValue}
        renderSuggestion={config?.renderSuggestion}
        alwaysRenderSuggestions={false}
        shouldRenderSuggestions={shouldRenderSuggestions}
        onSuggestionSelected={onSuggestionSelected}
        focusInputOnSuggestionClick={false}
        inputProps={{
          placeholder: "Type...",
          ...restprops,
          value: inputValue,
          onChange: onChangeS,
          onBlur: onBlurS,
          clearIt: resetSelection,
          showClose: !!value,
          loading,
        }}
        theme={{
          suggestionsContainer: "autosuggest-scrollbar",
          suggestionsList: "autosuggest-list-reset",
          suggestion: "autosuggest-suggestion",
          suggestionHighlighted: "autosuggest-suggestion-highlighted",
        }}
        renderInputComponent={renderInputComponent}
      />
    </Box>
  );
}

ServerAutoSuggest.propTypes = {
  config: PropTypes.shape({
    getSuggestionValue: PropTypes.func,
    renderSuggestion: PropTypes.func,
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  valueDisplay: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  renderPrint: PropTypes.func,
};

ServerAutoSuggest.defaultProps = {
  value: "",
  valueDisplay: null,
  onChange: null,
  onBlur: null,
  renderPrint: null,
};

export default React.memo(ServerAutoSuggest);
